class ResourceCarouselNavigation extends HTMLElement {
  connectedCallback() {
    this.shell = this.closest('.resource-list-carousel-shell');
    this.scroller = this.shell?.querySelector('slideshow-slides');
    this.previousButton = this.querySelector('[data-resource-carousel-direction="previous"]');
    this.nextButton = this.querySelector('[data-resource-carousel-direction="next"]');
    this.dotsContainer = this.querySelector('[data-resource-carousel-dots]');
    this.showDots = this.dataset.showDots === 'true';
    this.autoHide = this.dataset.autoHide === 'true';
    this.slideCount = Math.max(0, Number.parseInt(this.dataset.slideCount || '0', 10) || 0);
    this.desktopColumns = Math.max(1, Number.parseInt(this.dataset.desktopColumns || '1', 10) || 1);
    this.mobileColumns = Math.max(1, Number.parseInt(this.dataset.mobileColumns || '1', 10) || 1);

    if (!this.scroller) return;

    this.isRtl = getComputedStyle(this.scroller).direction === 'rtl';

    this.handlePrevious = () => this.scrollByPage(-1);
    this.handleNext = () => this.scrollByPage(1);
    this.handleScroll = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateState();
      });
    };
    this.handleResize = () => this.refresh();

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.scroller);
    } else {
      window.addEventListener('resize', this.handleResize, { passive: true });
    }

    requestAnimationFrame(() => this.refresh());
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.scroller?.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);

    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  get maxScroll() {
    if (!this.scroller) return 0;
    return Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
  }

  get scrollPosition() {
    if (!this.scroller) return 0;
    return Math.max(0, Math.abs(this.scroller.scrollLeft));
  }

  get visibleColumns() {
    return window.matchMedia('(min-width: 750px)').matches ? this.desktopColumns : this.mobileColumns;
  }

  get pageCount() {
    if (!this.scroller || this.slideCount <= this.visibleColumns) return 1;
    return Math.max(1, Math.ceil(this.slideCount / this.visibleColumns));
  }

  get hasUsefulOverflow() {
    // Fractional layout rounding can create 1–2px of scrollWidth even when every card is already visible.
    return this.pageCount > 1 && this.maxScroll > 4;
  }

  refresh() {
    this.renderDots();
    this.updateState();
  }

  scrollByPage(direction) {
    if (!this.scroller || !this.hasUsefulOverflow) return;

    const count = this.pageCount;
    const progress = this.maxScroll > 0 ? this.scrollPosition / this.maxScroll : 0;
    const currentPage = Math.round(progress * (count - 1));
    const nextPage = Math.min(count - 1, Math.max(0, currentPage + direction));

    this.scrollToPage(nextPage);
  }

  scrollToPage(pageIndex) {
    if (!this.scroller) return;

    const count = this.pageCount;
    const target = count > 1 ? this.maxScroll * (pageIndex / (count - 1)) : 0;
    const left = this.isRtl ? -target : target;

    this.scroller.scrollTo({ left, behavior: 'smooth' });
  }

  renderDots() {
    if (!this.dotsContainer) return;

    if (!this.showDots) {
      this.dotsContainer.hidden = true;
      this.dotsContainer.replaceChildren();
      return;
    }

    const count = this.pageCount;
    this.dotsContainer.replaceChildren();

    for (let index = 0; index < count; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'resource-carousel-navigation__dot';
      dot.setAttribute('aria-label', `Go to carousel page ${index + 1}`);
      dot.addEventListener('click', () => this.scrollToPage(index));
      this.dotsContainer.appendChild(dot);
    }

    this.dotsContainer.hidden = count <= 1;
  }

  updateState() {
    if (!this.scroller) return;

    const maxScroll = this.maxScroll;
    const currentScroll = this.scrollPosition;
    const hasUsefulOverflow = this.hasUsefulOverflow;
    const atStart = currentScroll <= 1;
    const atEnd = currentScroll >= maxScroll - 1;

    if (this.previousButton) this.previousButton.disabled = !hasUsefulOverflow || atStart;
    if (this.nextButton) this.nextButton.disabled = !hasUsefulOverflow || atEnd;

    // Never show dead navigation. This also avoids tiny fractional-pixel overflow
    // making arrows appear when the configured number of cards already fits.
    this.hidden = !hasUsefulOverflow;

    if (!this.dotsContainer) return;

    const dots = Array.from(this.dotsContainer.querySelectorAll('.resource-carousel-navigation__dot'));
    if (!dots.length) return;

    const progress = maxScroll > 0 ? currentScroll / maxScroll : 0;
    const activeIndex = Math.round(progress * (dots.length - 1));

    dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    });
  }
}

if (!customElements.get('resource-carousel-navigation')) {
  customElements.define('resource-carousel-navigation', ResourceCarouselNavigation);
}
